/**
 * Slot times on the wire.
 *
 * Availability arrives as garage-local date-times with no offset: "09:00" means
 * nine o'clock at the garage. Sending that back unchanged left the server to
 * guess the zone, and a guess in the server's own zone books the wrong hour for
 * half the year. The booking request therefore carries the offset the channel's
 * timezone has at that moment, so the instant cannot be misread.
 */

export const DEFAULT_CHANNEL_TIMEZONE = 'Europe/London'

const LOCAL_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d{1,9})?$/
const HAS_OFFSET = /(?:[zZ]|[+-]\d{2}:?\d{2})$/

/** Minutes the zone is ahead of UTC at the given instant. Throws for an unknown zone. */
function offsetMinutesAt(epochMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(epochMs))

  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value)

  const wallClockAsUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour') % 24,
    read('minute'),
    read('second')
  )

  return Math.round((wallClockAsUtc - Math.floor(epochMs / 1000) * 1000) / 60_000)
}

function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  const hours = String(Math.floor(abs / 60)).padStart(2, '0')
  const mins = String(abs % 60).padStart(2, '0')
  return `${sign}${hours}:${mins}`
}

/**
 * Appends the channel's UTC offset to a slot start, e.g. `2026-10-14T09:00:00`
 * in Europe/London becomes `2026-10-14T09:00:00+01:00`.
 *
 * A value that already carries an offset is returned as it is. An unknown zone,
 * or a value that cannot be parsed, is also returned unchanged: the backend reads
 * an offset-less time as channel-local, which is the right fallback.
 */
export function toChannelSlotStart(value: string, timeZone: string | null | undefined): string {
  const trimmed = value.trim()
  if (HAS_OFFSET.test(trimmed)) return trimmed

  const match = LOCAL_DATE_TIME.exec(trimmed)
  if (!match) return trimmed

  const [, year, month, day, hour, minute, second = '00'] = match
  const local = `${year}-${month}-${day}T${hour}:${minute}:${second}`
  const wallClockAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  )

  const zone = timeZone?.trim() || DEFAULT_CHANNEL_TIMEZONE

  try {
    // Guess from the wall-clock time read as UTC, then check the offset at the
    // instant that guess implies. The second look settles the days the clocks
    // change on.
    const firstGuess = offsetMinutesAt(wallClockAsUtc, zone)
    const offset = offsetMinutesAt(wallClockAsUtc - firstGuess * 60_000, zone)
    return `${local}${formatOffset(offset)}`
  } catch {
    return trimmed
  }
}

/**
 * The `slotStart` to book a slot with. The backend's own offset start (`startAt`) is sent back when
 * it gave one, so the booking means exactly the time shown; otherwise the offset is worked out from
 * the channel's timezone.
 */
export function bookingSlotStart(
  slot: { startTime: string; startAt?: string | null },
  timeZone: string | null | undefined
): string {
  const startAt = slot.startAt?.trim()
  return startAt || toChannelSlotStart(slot.startTime, timeZone)
}

/**
 * The calendar day a slot falls on, as a local midnight Date for the calendar.
 * Read from the string rather than through `new Date()`, which would shift a
 * late-evening slot into the next day for a visitor in another zone.
 */
export function slotDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim())
  if (!match) return null

  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? null : date
}
